import { useState } from 'react';
import { Button } from '~/components/button';
import { HALFMOON_SOLAR, ImageSelector, ImageType } from '~/components/image';
import { Inputs } from '~/components/inputs';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Description, Title } from '~/components/typography';
import { useCopy } from '~/hooks';
import { BillingAddressForm } from '~/machines/billing-address-selection';
import { UtilityAccountInfo } from '~/machines/enrollment-selection/types';

export function getConsolidatedAddress(utilityAccount: any, isResumingFlow = false): string {
  if (!utilityAccount) return '';
  if (isResumingFlow && utilityAccount?.serviceAddressStreetOne) {
    const {
      serviceAddressStreetOne,
      serviceAddressStreetTwo,
      serviceAddressCity,
      serviceAddressState,
      serviceAddressZip,
    } = utilityAccount;

    return [
      serviceAddressStreetOne,
      serviceAddressStreetTwo,
      serviceAddressCity,
      serviceAddressState,
      serviceAddressZip,
    ]
      .filter(Boolean)
      .join(', ');
  } else {
    const { address } = utilityAccount;
    return [address?.street, address?.streetTwo, address?.city, address?.state, address?.zipCode]
      .filter(Boolean)
      .join(', ');
  }
}

export function convertAddressTypeToString(address: any): string {
  if (!address) return '';
  return [address.street, address.streetTwo, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(', ');
}

export const BillingAddress = ({ context, next, overrideCopy }: BillingAddressProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const { utilityAccountInfo: utilityAccounts, shouldShowBillingAddressCheckbox } = context;
  const consolidatedAddresses = utilityAccounts?.map(utilityAccount =>
    getConsolidatedAddress(utilityAccount, Boolean(context?.resumingUser)),
  );

  const [form, setForm] = useState<BillingAddressForm>({
    address: {},
    addressSecondary: '',
    sameAsServiceAddress: utilityAccounts?.length === 1,
  });

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

  const handleChange = (key: string, value: string | boolean) => {
    switch (key) {
      case 'sameAsServiceAddress':
        setSubmitButtonDisabled(!(value as boolean));
        setForm(prev => ({
          ...prev,
          sameAsServiceAddress: value as boolean,
        }));
        break;

      case 'address':
        if (typeof value === 'string') {
          setSubmitButtonDisabled(false);
          setForm(prev => ({
            ...prev,
            address: JSON.parse(value),
          }));
        }
        break;

      case 'addressSecondary':
        setForm(prev => ({
          ...prev,
          addressSecondary: value as string,
        }));
        break;

      default:
        break;
    }
  };

  const handleSubmit = () => {
    const isUsingServiceAddress = form.sameAsServiceAddress;
    const primaryAccount = utilityAccounts?.[0];

    const data =
      isUsingServiceAddress && primaryAccount
        ? {
            ...form,
            address: primaryAccount?.address || {},
            addressSecondary: primaryAccount?.addressSecondary,
          }
        : form;

    next(data);
  };

  return (
    <TwoColumnGrid testId="screen-Mailingaddress">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={4}>{copy.descriptionOne}</Description>
        <Description mb={4}>{copy.descriptionTwo}</Description>

        <Container paddingTop="12px">
          {consolidatedAddresses?.map((address, index) => (
            <div
              key={address}
              style={{
                backgroundColor: '#F3F5F2',
                padding: '16px',
                marginBottom: '16px',
                borderRadius: '12px',
              }}
            >
              <p style={{ fontWeight: '600', margin: 0 }}>Utility account #{index + 1}</p>
              <p style={{ color: '#4B5563', marginTop: '8px' }}>Service address: {address}</p>
            </div>
          ))}
        </Container>

        <Container addInputMargin marginY={7}>
          {!!shouldShowBillingAddressCheckbox && (
            <Inputs.BillingAddressCheckbox
              onChange={value => handleChange('sameAsServiceAddress', value)}
              value={form.sameAsServiceAddress}
            />
          )}

          {(!form.sameAsServiceAddress || !shouldShowBillingAddressCheckbox) && (
            <>
              <Inputs.Address
                label="Mailing address"
                onChange={value => handleChange('address', JSON.stringify(value))}
              />
              <Inputs.AddressSecondary
                onChange={value => handleChange('addressSecondary', value)}
                value={form.addressSecondary}
              />
            </>
          )}
        </Container>

        <Button
          style={{ width: '100%' }}
          disabled={submitButtonDisabled}
          type="submit"
          mode="primary"
          onClick={handleSubmit}
          testId="billing-address-submit"
        >
          Continue
        </Button>
      </TwoColumnGrid.Primary>

      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = () => ({
  title: 'Add your mailing address',
  descriptionOne:
    'Please submit the mailing address that you would like to use for the utility account(s) provided. ',
  descriptionTwo:
    'We collect your mailing address in case we need to reach you by mail. Your utility bill will still come directly from your utility provider, as it does today.',
  imageType: HALFMOON_SOLAR as ImageType,
  next: 'Continue',
});

export type BillingAddressCopy = ReturnType<typeof defaultCopy>;

export interface BillingAddressProps {
  context: BillingAddressContext;
  next: (payload: BillingAddressForm) => void;
  overrideCopy?: (context: BillingAddressContext) => Partial<BillingAddressCopy>;
}

export type BillingAddressContext = {
  funnelId?: string;
  utilityAccountInfo?: UtilityAccountInfo[];
  shouldShowBillingAddressCheckbox?: boolean;
  resumingUser?: any;
};
